/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch } from "react-redux";
import { notifyError, notifyInfo, notifySuccess } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { hasElements } from "utils/arrays";
import FolderIcon from "@material-ui/icons/Folder";
import { Breadcrumbs, Checkbox, Grid, IconButton, Link, ListItemSecondaryAction, Typography } from "@material-ui/core";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DeleteIcon from "@material-ui/icons/Delete";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import Input from "components/common_components/Input/Input";
import { sanitizeFileName } from "utils/files";
import { ConfirmDialog } from "components/common_components/Dialogs";
import AppContext from "AppContext";

const FileExplorer = ({
    rootPath,
    onDirectoryChange = () => {},
    rootTitle,
    showFiles = true,
    showDirectories = true,
    onFileSelected = () => {},
    selectedFile = undefined,
    onDeleteFile = () => {},
    attachmentRecords = [],
    allowCreateFolder = true,
    allowAttachFiles = true,
    allowMoveFiles = true,
    initAttachFiles = () => {},
    initMoveFiles = () => {},
    isMovingFilesMode = false,
    disableFiles = false,
    selectedFilesToMove = [],
    onFilesToMoveChange = () => {},
    showToolbar = true,
}) => {
    const dispatch = useDispatch();
    const {
        api: {
            General: { Storage },
        },
    } = useContext(AppContext);
    const [storageContents, setStorageContents] = useState({ directories: [], files: [] });
    const [storageLoading, setStorageLoading] = useState(false);
    const [currentDirectory, setCurrentDirectory] = useState(rootPath);
    const [createFolderMode, setCreateFolderMode] = useState(false);
    const [createFolderName, setCreateFolderName] = useState("");
    const [createFolderLoading, setCreateFolderLoading] = useState(false);
    const isFirstRender = useRef(true);
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    const isFileSelectedToMove = (file) => {
        return selectedFilesToMove.some((f) => f.id === file.id);
    };

    const onFileSelectedToMoveChange = (file) => {
        if (isFileSelectedToMove(file)) {
            onFilesToMoveChange((prev) => prev.filter((f) => f.id !== file.id));
        } else {
            onFilesToMoveChange((prev) => [...prev, file]);
        }
    };

    useEffect(() => {
        onFilesToMoveChange(selectedFilesToMove);
    }, [selectedFilesToMove]);

    useEffect(() => {
        if (currentDirectory) {
            getStorageContents(currentDirectory);
            onDirectoryChange(currentDirectory);
        }
    }, [currentDirectory]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        getStorageContents(currentDirectory);
    }, [attachmentRecords]);

    const getStorageContents = async (directory) => {
        try {
            setStorageLoading(true);
            const contents = await Storage.listDirectoryContents(directory, { fetchUrls: false });
            setStorageContents(contents);
        } catch (error) {
            console.error(error);
        } finally {
            setStorageLoading(false);
        }
    };

    const goToFolder = (newFolderPath) => {
        setCurrentDirectory(newFolderPath);
    };

    const goToParentFolder = () => {
        const parentPath = currentDirectory.split("/").slice(0, -1).join("/");
        setCurrentDirectory(parentPath);
    };

    function handleClick(event) {
        event.preventDefault();
    }

    const getBreadcrumbItems = () => {
        const parts = currentDirectory
            .replace(rootPath, "")
            .split("/")
            .filter((part) => part);

        if (hasElements(parts)) {
            const breadcrumbItems = parts.reduce((arr, part, index) => {
                const path = rootPath + "/" + parts.slice(0, index + 1).join("/");
                return [...arr, { part, path, isLast: index === parts.length - 1 }];
            }, []);

            return breadcrumbItems;
        } else return [];
    };

    function initCreateFolder() {
        setCreateFolderMode(true);
    }

    const cancelCreateFolder = () => {
        setCreateFolderMode(false);
        setCreateFolderName("");
        setCreateFolderLoading(false);
    };

    const onCreateFolder = async () => {
        if (createFolderName.trim()) {
            setCreateFolderLoading(true);
            const sanitizedFolderName = sanitizeFileName(createFolderName);

            // Check if the folder already exists
            if (storageContents.directories.includes(sanitizedFolderName)) {
                dispatch(notifyError("Folder already exists!"));
                return;
            }

            // Create the folder in Firebase Storage
            const newFolderPath = `${currentDirectory}/${sanitizedFolderName}`;
            await Storage.createEmptyFolder(newFolderPath);

            // Reset the folder creation state
            setCreateFolderMode(false);
            setCreateFolderName("");
            setCreateFolderLoading(false);

            // Refresh the storage contents
            await getStorageContents(currentDirectory);
            dispatch(notifySuccess("Folder created successfully!"));

            // Navigate into the new folder
            goToFolder(newFolderPath);
        }
    };

    const onDeleteFolderHandler = () => {
        if (
            storageContents.directories.length > 0 ||
            storageContents.files.filter((x) => !x.fullPath.endsWith(".placeholder")).length > 0
        ) {
            dispatch(notifyError("Cannot delete a folder that is not empty!"));
            return;
        }
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: "Delete folder",
            content: `Are you sure you want to delete this folder? This action cannot be undone.`,
            primaryButtonText: "Yes, delete",
            onPrimaryClick: async () => {
                await Storage.deleteAsync({
                    path: `${currentDirectory}/.placeholder`,
                });
                dispatch(notifyInfo("Folder deleted successfully!"));
                goToParentFolder();
                prev.onClose();
            },
        }));
    };

    const onFileSelectedHandler = (file) => {
        if (disableFiles) return;
        if (isMovingFilesMode) return;
        onFileSelected(file);
    };

    const showAttachFilesButton = allowAttachFiles && !createFolderMode && !isMovingFilesMode;
    const showMoveFilesButton = allowMoveFiles && !createFolderMode && !isMovingFilesMode;
    const showCreateFolderButton = allowCreateFolder && !createFolderMode;
    const showDeleteFolderButton = rootPath !== currentDirectory && !createFolderMode;

    return (
        <div className="file-explorer" style={{ borderWidth: 1, borderStyle: "solid", borderColor: "rgba(0, 0, 0, 0.12)" }}>
            {showToolbar && (
                <div className="button-group toolbar">
                    {showAttachFilesButton && (
                        <Button secondary small onClick={initAttachFiles}>
                            <Icon name="attachment" size="sm" color="white" />
                            <span>Attach files</span>
                        </Button>
                    )}
                    {showMoveFilesButton && (
                        <Button secondary small outline onClick={initMoveFiles}>
                            <Icon name="move-to-folder" size="lg" color="secondary" />
                            <span>Move files</span>
                        </Button>
                    )}
                    {showCreateFolderButton && (
                        <Button secondary small outline onClick={initCreateFolder}>
                            <Icon name="folder" size="sm" color="secondary" />
                            <span>Create folder</span>
                        </Button>
                    )}
                    {createFolderMode && (
                        <>
                            <Input
                                value={createFolderName}
                                onChange={(e) => setCreateFolderName(e.target.value)}
                                placeholder="New folder"
                            />
                            <Button secondary small onClick={onCreateFolder} loading={createFolderLoading}>
                                <span>OK</span>
                            </Button>
                            <Button secondary outline small onClick={cancelCreateFolder}>
                                <Icon name="cancel" size="sm" color="secondary" />
                            </Button>
                        </>
                    )}
                    {showDeleteFolderButton && (
                        <Button secondary small outline onClick={onDeleteFolderHandler}>
                            <Icon name="cancel" size="sm" color="secondary" />
                            <span>Delete folder</span>
                        </Button>
                    )}
                </div>
            )}

            <div role="presentation" onClick={handleClick} className="breadcrumbs-container">
                <Breadcrumbs maxItems={3} aria-label="breadcrumb">
                    <Link
                        underline="hover"
                        style={{ display: "flex", alignItems: "center" }}
                        color="inherit"
                        href="/"
                        onClick={() => goToFolder(rootPath)}
                    >
                        <FolderIcon style={{ marginRight: 5 }} />
                        {rootTitle}
                    </Link>
                    {getBreadcrumbItems().map(({ part, path, isLast }, index) =>
                        isLast ? (
                            <Typography key={index} color="primary" style={{ display: "flex", alignItems: "center" }}>
                                {part}
                            </Typography>
                        ) : (
                            <Link
                                key={index}
                                underline="hover"
                                style={{ display: "flex", alignItems: "center" }}
                                color="inherit"
                                href="#"
                                onClick={() => goToFolder(path)}
                            >
                                {part}
                            </Link>
                        ),
                    )}
                </Breadcrumbs>
            </div>
            {storageLoading ? (
                <LoadingPage />
            ) : (
                <List dense>
                    {currentDirectory !== rootPath && (
                        <Grid container alignItems="center" spacing={1}>
                            {isMovingFilesMode && <Grid item xs={1} />}
                            <Grid item xs={11}>
                                <ListItem key={"root-directory"} button onClick={goToParentFolder}>
                                    <ListItemIcon>
                                        <FolderIcon style={{ color: "rgba(234, 188, 48, 0.87)" }} />
                                    </ListItemIcon>
                                    <ListItemText primary="..." primaryTypographyProps={{ style: { fontSize: 12 } }} />
                                </ListItem>
                            </Grid>
                        </Grid>
                    )}
                    {showDirectories &&
                        hasElements(storageContents.directories) &&
                        storageContents.directories.map((dir, index) => (
                            <Grid container key={`subdirectory-${index}`} alignItems="center" spacing={1}>
                                {isMovingFilesMode && <Grid item xs={1} />}
                                <Grid item xs={11}>
                                    <ListItem button onClick={() => goToFolder(currentDirectory + "/" + dir)}>
                                        <ListItemIcon>
                                            <FolderIcon style={{ color: "rgba(234, 188, 48, 0.87)" }} />
                                        </ListItemIcon>
                                        <ListItemText primary={dir} primaryTypographyProps={{ style: { fontSize: 12 } }} />
                                    </ListItem>
                                </Grid>
                            </Grid>
                        ))}
                    {showFiles &&
                        hasElements(attachmentRecords) &&
                        hasElements(storageContents.files) &&
                        attachmentRecords
                            .filter((x) => storageContents.files.some((sf) => x.url.includes(encodeURIComponent(sf.fullPath))))
                            .map((file, index) => (
                                <Grid container key={`file-container-${index}`} alignItems="center" spacing={1}>
                                    {isMovingFilesMode && (
                                        <Grid item xs={1}>
                                            <Checkbox
                                                checked={isFileSelectedToMove(file)}
                                                onChange={() => onFileSelectedToMoveChange(file)}
                                                color="primary"
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={11}>
                                        <ListItem
                                            key={`file-${index}`}
                                            button
                                            onClick={() => onFileSelectedHandler(file)}
                                            selected={selectedFile && file.id === selectedFile.id}
                                            disabled={disableFiles}
                                        >
                                            <ListItemIcon>
                                                <PictureAsPdfIcon style={{ color: "rgba(187, 9, 9, 0.87)" }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={file.dateString}
                                                primaryTypographyProps={{ style: { fontSize: 12 } }}
                                                secondaryTypographyProps={{ style: { fontSize: 10 } }}
                                            />
                                            {!disableFiles && !isMovingFilesMode && (
                                                <ListItemSecondaryAction>
                                                    <IconButton edge="end" aria-label="delete" onClick={() => onDeleteFile(file)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            )}
                                        </ListItem>
                                    </Grid>
                                </Grid>
                            ))}
                </List>
            )}
            <ConfirmDialog {...confirmDialogProps} />
        </div>
    );
};

export default FileExplorer;
